import styled from "@emotion/styled"
import { EngineGroup } from "@eurodrive/utils"
import { Typography } from "@mui/material"
import { Dispatch, SetStateAction, useEffect } from "react"
import { Config, EngineOption, formatPrice } from "./CarConfiguration"

const StyledLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`

const OptionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const StyledFieldset = styled.fieldset`
  border: none;
  padding: 0;
  margin: 0;
`

const StyledLegend = styled.legend`
  padding: 0;
  margin-bottom: 20px;
`

const Option = styled.label<{ selected: boolean }>`
  background-color: transparent;
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-radius: 8px;
  border: 1px solid ${({ selected }) => (selected ? "#000" : "#ece9e9")};
  cursor: pointer;
`

const HiddenRadio = styled.input`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
`

const PricesFlex = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`

export default function FirstStage({
  markComplete,
  options,
  engineGroups = [],
  config,
  setConfig,
  isLeasing = false,
}: {
  markComplete: () => void
  options: EngineOption[]
  engineGroups?: EngineGroup[]
  config: Config
  setConfig: Dispatch<SetStateAction<Config>>
  isLeasing?: boolean
}) {
  // Auto-select when there's only one option
  const displayOptions = !isLeasing && engineGroups.length > 0
    ? engineGroups.map((group) => ({
        engine: group.engineLabel,
        price: group.trims[0].price,
        oldPrice: group.trims[0].oldPrice,
        vehicleNumber: group.trims[0].vehicleNumber,
      }))
    : options

  useEffect(() => {
    if (displayOptions.length === 1 && config.engine === null) {
      handleSelect(0)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // For sales with engineGroups, show one option per engine with base (cheapest) price
  // Auto-select trim index 0 when engine is selected
  const handleSelect = (index: number) => {
    if (!isLeasing && engineGroups.length > 0) {
      // Sales flow with engine groups: set engine and auto-select cheapest trim
      setConfig((prev) => ({
        ...prev,
        engine: index,
        trim: 0, // Auto-select cheapest trim
        // Color options depend on the selected trim, so an engine change
        // (which resets the trim) invalidates any previous color choice
        color: null,
      }))
    } else {
      // Leasing flow or no engine groups: just set engine
      setConfig((prev) => ({
        ...prev,
        engine: index,
      }))
    }
    markComplete()
  }

  const getAriaLabel = (option: EngineOption) => {
    const priceText = option.oldPrice
      ? `מחיר ${formatPrice(option.price)}, לפני הנחה ${formatPrice(option.oldPrice)}`
      : `מחיר ${formatPrice(option.price)}`
    return `${option.engine}, ${priceText}`
  }

  return (
    <StyledLayout>
      <StyledFieldset>
        <StyledLegend>
          <Typography variant="h2" component="h2">
            בחירת מנוע
          </Typography>
        </StyledLegend>
        <OptionsList role="radiogroup">
          {displayOptions.map((el, i) => (
            <Option key={i} selected={config.engine === i}>
              <HiddenRadio
                type="radio"
                name="engine"
                value={i}
                checked={config.engine === i}
                onChange={() => handleSelect(i)}
                aria-label={getAriaLabel(el)}
              />
              <Typography>{el.engine}</Typography>
              <PricesFlex>
                {el.oldPrice && (
                  <Typography
                    variant="body3"
                    style={{ textDecoration: "line-through" }}
                    aria-hidden="true"
                  >
                    {formatPrice(el.oldPrice)}
                  </Typography>
                )}
                <Typography variant="body1" aria-hidden="true">
                  {formatPrice(el.price)}
                </Typography>
              </PricesFlex>
            </Option>
          ))}
        </OptionsList>
      </StyledFieldset>
    </StyledLayout>
  )
}
